import intlTelInput from 'intl-tel-input';
import {
    ar,
    da,
    de,
    en,
    es,
    fa,
    fr,
    nl,
    pl,
    ru,
    tr,
    zh,
} from 'intl-tel-input/locale';
import 'intl-tel-input/styles';
import '../css/auth-phone.css';

/**
 * HOME auth international phone input
 *
 * - Individual / consultant / agency / construction forms use the same behaviour.
 * - UI language is read from the active Laravel locale.
 * - Country names, Search, No results and ARIA strings are localised by intl-tel-input.
 * - <= 991px uses a fullscreen country selector so it cannot overflow the viewport.
 * - Desktop uses a detached dropdown appended to body so parent overflow cannot clip it.
 * - Backend always receives an E.164 phone number.
 * - The existing one-click submit behaviour is preserved.
 */

const PHONE_UI_TRANSLATIONS = {
    ar,
    da,
    de,
    en,
    es,
    fa,
    fr,
    nl,
    pl,
    ru,
    tr,
    zh,
};

const normaliseLocale = (value) => {
    const raw = String(value || 'en')
        .trim()
        .toLowerCase()
        .replace('_', '-');

    // Laravel locales in this project are simple ISO codes, but keep this safe
    // for values such as zh-CN / en-US.
    const base = raw.split('-')[0];

    return Object.prototype.hasOwnProperty.call(PHONE_UI_TRANSLATIONS, base)
        ? base
        : 'en';
};

const isCompactViewport = () => (
    window.matchMedia('(max-width: 991px)').matches
);

document.addEventListener('DOMContentLoaded', () => {
    const inputs = Array.from(document.querySelectorAll('.js-intl-phone'));

    inputs.forEach((input) => {
        if (!(input instanceof HTMLInputElement)) {
            return;
        }

        // Guard against accidental double initialisation by duplicated Vite entry loading.
        if (input.dataset.phoneInitialised === '1') {
            return;
        }
        input.dataset.phoneInitialised = '1';

        const form = input.closest('form');

        if (!form) {
            return;
        }

        const defaultCountry = (
            input.dataset.defaultCountry || 'tr'
        ).toLowerCase();

        const locale = normaliseLocale(
            input.dataset.locale || document.documentElement.lang || 'en'
        );

        const invalidMessage =
            input.dataset.invalidMessage ||
            'Please enter a valid mobile phone number.';

        const loadErrorMessage =
            input.dataset.loadError ||
            'Phone validation could not be loaded. Please refresh the page and try again.';

        const initialValue = input.value.trim();
        const compactSelector = isCompactViewport();

        const iti = intlTelInput(input, {
            initialCountry: defaultCountry,
            separateDialCode: true,
            strictMode: true,
            allowedNumberTypes: [
                'MOBILE',
                'FIXED_LINE_OR_MOBILE',
            ],
            placeholderNumberType: 'MOBILE',

            // Deterministic responsive behaviour:
            // mobile/tablet = fullscreen, desktop = dropdown.
            countrySelectorMode: compactSelector
                ? 'FULLSCREEN'
                : 'DROPDOWN',
            countrySearch: true,
            matchDropdownWidth: true,

            // On desktop, keeping the selector under body prevents clipping by
            // auth card / layout overflow and lets the library reposition with
            // fixed coordinates next to the input.
            dropdownParent: document.body,

            // Current intl-tel-input v29 localisation API.
            uiTranslations: PHONE_UI_TRANSLATIONS[locale],

            numberDisplayFormat: 'NATIONAL',
            placeholderNumberPolicy: 'POLITE',

            classNames: {
                countrySelector: 'hm-phone-country-selector',
                countrySelectorContainer: 'hm-phone-country-selector-container',
                searchInput: 'hm-phone-country-search',
            },

            loadUtils: () => import('intl-tel-input/utils'),
        });

        const section = input.closest('.hm-phone-section');

        let error = section?.querySelector('.hm-phone-error');

        if (!error) {
            error = document.createElement('small');
            error.className = 'hm-phone-error';
            error.hidden = true;
            error.textContent = invalidMessage;
            section?.appendChild(error);
        }

        const clearError = () => {
            input.setCustomValidity('');

            if (error) {
                error.hidden = true;
            }
        };

        const showError = (message = invalidMessage) => {
            input.setCustomValidity(message);

            if (error) {
                error.textContent = message;
                error.hidden = false;
            }
        };

        const syncVisualState = () => {
            section?.classList.toggle(
                'has-phone-value',
                input.value.trim() !== ''
            );
        };

        const originalName =
            input.getAttribute('name') ||
            input.dataset.originalName ||
            'phone';

        input.dataset.originalName = originalName;

        let hiddenPhoneInput = form.querySelector(
            `input[type="hidden"][data-phone-hidden="${originalName}"]`
        );

        if (!hiddenPhoneInput) {
            hiddenPhoneInput = document.createElement('input');
            hiddenPhoneInput.type = 'hidden';
            hiddenPhoneInput.name = originalName;
            hiddenPhoneInput.dataset.phoneHidden = originalName;
            input.insertAdjacentElement('afterend', hiddenPhoneInput);
        }

        // The visual/national input must not be posted separately.
        input.removeAttribute('name');

        const submitButton = form.querySelector(
            'button[type="submit"], input[type="submit"]'
        );

        let submitting = false;

        const lockSubmit = () => {
            submitting = true;

            if (submitButton) {
                submitButton.disabled = true;
                submitButton.setAttribute('aria-busy', 'true');
            }
        };

        const unlockSubmit = () => {
            submitting = false;

            if (submitButton) {
                submitButton.disabled = false;
                submitButton.removeAttribute('aria-busy');
            }
        };

        iti.promise
            .then(() => {
                if (initialValue !== '') {
                    try {
                        iti.setNumber(initialValue);
                    } catch (error_) {
                        console.warn(
                            'Initial phone number could not be formatted.',
                            error_
                        );
                    }
                }

                syncVisualState();
            })
            .catch((error_) => {
                console.error('Phone module could not be initialised.', error_);
            });

        input.addEventListener('input', () => {
            clearError();
            hiddenPhoneInput.value = '';
            syncVisualState();
        });

        input.addEventListener('countrychange', () => {
            clearError();
            hiddenPhoneInput.value = '';
            syncVisualState();
        });

        input.addEventListener('open:countryselector', () => {
            document.documentElement.classList.add('hm-phone-country-open');
            document.body.classList.add('hm-phone-country-open');
        });

        input.addEventListener('close:countryselector', () => {
            document.documentElement.classList.remove('hm-phone-country-open');
            document.body.classList.remove('hm-phone-country-open');
        });

        syncVisualState();

        form.addEventListener('submit', async (event) => {
            if (submitting) {
                event.preventDefault();
                return;
            }

            event.preventDefault();

            clearError();
            lockSubmit();

            try {
                await iti.promise;

                if (input.value.trim() === '') {
                    showError();
                    unlockSubmit();
                    input.reportValidity();
                    input.focus();
                    return;
                }

                if (!iti.isValidNumber()) {
                    showError();
                    unlockSubmit();
                    input.reportValidity();
                    input.focus();
                    return;
                }

                const type = iti.getNumberType();

                if (
                    typeof type === 'string' &&
                    ![
                        'MOBILE',
                        'FIXED_LINE_OR_MOBILE',
                    ].includes(type)
                ) {
                    showError();
                    unlockSubmit();
                    input.reportValidity();
                    input.focus();
                    return;
                }

                const e164Number = iti.getNumber();

                if (
                    !e164Number ||
                    typeof e164Number !== 'string' ||
                    !e164Number.startsWith('+')
                ) {
                    showError();
                    unlockSubmit();
                    input.reportValidity();
                    input.focus();
                    return;
                }

                hiddenPhoneInput.value = e164Number;

                // Bypass a second submit event. This keeps the form responsive
                // on the first click while still sending the validated E.164 value.
                HTMLFormElement.prototype.submit.call(form);
            } catch (error_) {
                console.error('Phone validation module could not be loaded.', error_);
                unlockSubmit();
                showError(loadErrorMessage);
                input.reportValidity();
                input.focus();
            }
        });
    });
});
